import { useState } from "react";
import { Trash2, Plus, X, Link, ExternalLink, CopyCheck } from "lucide-react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import { AddUserModal } from "./AddUserModal";
import AdLinkServices from "../../../../../services/AdLinkServices";
import { useQueryClient } from "@tanstack/react-query";
import {
  generateContinueRegistrationLink,
  copyToClipboard,
} from "@/utils/encryptUtils";
import { useTranslation } from "react-i18next";

interface SharedUser {
  id: number;
  name: string;
  last_name: string;
  email: string;
  filePath: string | null;
}

interface AdLinkLink {
  nome: string;
  link: string;
}

interface ResumoAdLinkProps {
  sharedUsers?: SharedUser[] | string | { data?: SharedUser[] };
  isOpen: boolean;
  onToggle: () => void;
  deskId: number;
  itemType?: "desk" | "reunion";
  links?: AdLinkLink[] | string | { data?: AdLinkLink[] };
  links_adlink?: AdLinkLink[] | string | { data?: AdLinkLink[] };
}

function normalizeArray<T>(value?: T[] | string | { data?: T[] }): T[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  if (Array.isArray(value?.data)) return value.data;
  return [];
}

function resolveNormalizedLinks({
  itemType,
  links,
  links_adlink,
}: Pick<ResumoAdLinkProps, "itemType" | "links" | "links_adlink">) {
  if (itemType === "desk" && links_adlink !== undefined) {
    return normalizeArray<AdLinkLink>(links_adlink);
  }

  return normalizeArray<AdLinkLink>(links);
}

export function ResumoAdLink({
  sharedUsers = [],
  isOpen,
  onToggle,
  deskId,
  itemType = "desk",
  links = [],
  links_adlink,
}: ResumoAdLinkProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLinksModalOpen, setIsLinksModalOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSavingLinks, setIsSavingLinks] = useState(false);
  const [tempLinks, setTempLinks] = useState<AdLinkLink[]>([]);
  const queryClient = useQueryClient();
  const normalizedSharedUsers = normalizeArray<SharedUser>(sharedUsers);
  const normalizedLinks = resolveNormalizedLinks({
    itemType,
    links,
    links_adlink,
  });
  const currentSharedUserIds = normalizedSharedUsers.map((user) => user.id);

  const handleOpenLinksModal = () => {
    setTempLinks([...normalizedLinks]);
    setIsLinksModalOpen(true);
  };

  const handleAddLink = () => {
    setTempLinks([...tempLinks, { nome: "", link: "" }]);
  };

  const handleRemoveLink = (index: number) => {
    const updatedLinks = tempLinks.filter((_, i) => i !== index);
    setTempLinks(updatedLinks);
  };

  const handleLinkChange = (
    index: number,
    field: "nome" | "link",
    value: string,
  ) => {
    const updatedLinks = [...tempLinks];
    updatedLinks[index][field] = value;
    setTempLinks(updatedLinks);
  };

  const handleSaveLinks = async () => {
    // Validação: remover links vazios
    const validLinks = tempLinks.filter(
      (link) => link.nome.trim() && link.link.trim(),
    );

    setIsSavingLinks(true);
    try {
      const response =
        itemType === "desk"
          ? await AdLinkServices.updateLinksAdLink(deskId, validLinks)
          : await AdLinkServices.updateReunionLinks(deskId, validLinks);

      if (response.status === 200) {
        toast.success(t("resumoDesk.adLink.links_saved_success"));
        setIsLinksModalOpen(false);
        queryClient.invalidateQueries({ queryKey: ["dashboardData"] });
        queryClient.invalidateQueries({ queryKey: ["jobs"] });
        queryClient.invalidateQueries({ queryKey: ["reunioes_midia"] });
      } else {
        toast.error(t("resumoDesk.adLink.errors.save_links"));
      }
    } catch (error) {
      toast.error(t("resumoDesk.adLink.errors.save_links"));
      console.error(error);
    } finally {
      setIsSavingLinks(false);
    }
  };

  const handleShareAdLink = async () => {
    try {
      const baseUrl = window.location.origin;
      const adLinkUrl = `${baseUrl}/ad-link/${deskId}`;
      await navigator.clipboard.writeText(adLinkUrl);
      toast.success(t("resumoDesk.adLink.link_copied_success"));
    } catch (error) {
      toast.error(t("resumoDesk.adLink.errors.copy_link"));
    }
  };

  const handleCopyRegistrationLink = async (
    email: string,
    isAdLink: boolean,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    try {
      const continueLink = generateContinueRegistrationLink(email, isAdLink);
      const success = await copyToClipboard(continueLink);

      if (success) {
        toast.success(t("resumoDesk.adLink.registration_link_copied"));
      } else {
        toast.error(t("resumoDesk.adLink.errors.copy_link"));
      }
    } catch (error) {
      toast.error(t("resumoDesk.adLink.errors.generate_registration_link"));
    }
  };

  const handleRemoveUser = (user: SharedUser) => {
    const displayName =
      user.name && user.last_name
        ? `${user.name} ${user.last_name}`
        : user.name
          ? user.name
          : user.email;

    confirmAlert({
      title: t("resumoDesk.adLink.remove_user"),
      message: t("resumoDesk.adLink.remove_user_confirmation", {
        name: displayName,
      }),
      buttons: [
        {
          label: t("common:yes"),
          onClick: async () => {
            setIsRemoving(true);
            try {
              const updatedUsers = currentSharedUserIds.filter(
                (id) => id !== user.id,
              );

              const response =
                itemType === "desk"
                  ? await AdLinkServices.updateSharedUsers(deskId, updatedUsers)
                  : await AdLinkServices.updateReunionSharedUsers(
                      deskId,
                      updatedUsers,
                    );

              if (response.status === 200 || response.status === 201) {
                toast.success(t("resumoDesk.adLink.user_removed_success"));
                queryClient.invalidateQueries({ queryKey: ["dashboardData"] });
                queryClient.invalidateQueries({ queryKey: ["jobs"] });
                queryClient.invalidateQueries({ queryKey: ["reunioes_midia"] });
              } else {
                toast.error(t("resumoDesk.adLink.errors.remove_user"));
              }
            } catch (error) {
              toast.error(t("resumoDesk.adLink.errors.remove_user"));
            } finally {
              setIsRemoving(false);
            }
          },
        },
        {
          label: t("common:cancel"),
          onClick: () => {},
        },
      ],
    });
  };
  return (
    <>
      <div className="sm:max-w-72 w-[95vw] bg-white p-4 sm:p-7 rounded-3xl shadow-xl relative transition-all duration-300 ease-in-out space-y-3 text-sm">
        <div className="flex gap-2 items-center justify-between">
          <div className="flex gap-2 items-center">
            <svg
              width="40"
              height="15"
              viewBox="0 0 40 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_9485_619)">
                <path
                  d="M12.7689 11.7687H6.20092L7.03876 9.8637H11.9298L8.32771 1.61057L2.46155 15H0L5.9389 1.43182C6.14896 0.779853 6.68284 0 7.39468 0H9.25945C10.0453 0 10.4432 0.799754 10.7052 1.43182C14.6645 10.4847 12.6848 5.95835 16.6441 15H14.1826L12.7689 11.7687Z"
                  fill="black"
                />
                <path
                  d="M18.2109 4.31536C18.2109 1.93599 20.1386 0 22.4953 0H25.8058C29.838 0 32.7184 3.34791 32.7184 7.31554V7.68427C32.7184 11.6631 29.838 14.9998 25.8058 14.9998H18.2122V4.31536H18.2109ZM20.4637 13.0417H25.3139C28.3934 13.0417 30.394 10.4836 30.394 7.49447C30.394 4.50534 28.3934 1.95848 25.3139 1.95848H20.4637V13.0417Z"
                  fill="black"
                />
                <path
                  d="M39.9959 7.33214C39.9959 4.11761 37.3718 1.51172 34.1347 1.51172C30.8976 1.51172 28.2734 4.11761 28.2734 7.33214C28.2734 10.5467 30.8976 13.1526 34.1347 13.1526C37.3718 13.1526 39.9959 10.5467 39.9959 7.33214Z"
                  fill="#153ECE"
                />
                <path
                  d="M32.4253 10.5119C32.1468 10.5119 31.8658 10.4306 31.6201 10.2613L31.5683 10.2255C30.9276 9.78361 30.7682 8.9061 31.2132 8.26979L32.563 6.48122C32.7745 6.17845 33.098 5.97207 33.47 5.90554C33.8419 5.83884 34.2177 5.9201 34.5281 6.13423L34.5799 6.16979C34.8434 6.35149 35.0386 6.61979 35.1298 6.92514C35.16 7.02704 35.1016 7.13411 34.9989 7.16433C34.8965 7.19418 34.7885 7.1365 34.7581 7.03441C34.6911 6.8096 34.553 6.61998 34.3589 6.48619L34.3071 6.45063C34.0815 6.2951 33.8085 6.23595 33.5387 6.28441C33.2687 6.33288 33.0338 6.48269 32.8772 6.70659L31.5275 8.49498C31.2085 8.95143 31.3241 9.58847 31.7895 9.90947L31.8413 9.94522C32.3065 10.266 32.948 10.1512 33.2713 9.68927L33.6836 9.09977C33.7447 9.01261 33.8653 8.99123 33.9534 9.05149C34.0412 9.11212 34.0631 9.23208 34.0021 9.31943L33.5897 9.90874C33.3153 10.3012 32.8738 10.5119 32.4255 10.5119H32.4253Z"
                  fill="white"
                />
                <path
                  d="M34.5565 8.77795C34.2778 8.77795 33.9961 8.69668 33.75 8.52715L33.6979 8.49103C33.4346 8.30934 33.2395 8.0414 33.1486 7.73624C33.1183 7.63434 33.1768 7.52727 33.2794 7.49705C33.3824 7.46701 33.49 7.52506 33.5203 7.62696C33.5871 7.8516 33.7252 8.04122 33.9193 8.175L33.9714 8.21112C34.4364 8.53176 35.0779 8.41677 35.4012 7.95479L36.7508 6.16603C37.07 5.70958 36.9542 5.07254 36.489 4.75154L36.4376 4.71634C36.2116 4.56044 35.939 4.50166 35.6686 4.54975C35.3986 4.59822 35.1637 4.74803 35.0071 4.97193L34.5947 5.56124C34.5337 5.64859 34.4129 5.67015 34.3251 5.60952C34.2371 5.54889 34.2154 5.42893 34.2765 5.34177L34.6888 4.75246C34.9044 4.44416 35.2279 4.23759 35.5999 4.17107C35.9718 4.10455 36.3478 4.18581 36.6582 4.39994L36.7095 4.43513C37.351 4.87758 37.5102 5.75491 37.065 6.39122L35.7154 8.17998C35.4435 8.56843 35.0037 8.77776 34.5565 8.77776V8.77795Z"
                  fill="white"
                />
                <path
                  d="M35.761 7.05141C35.7407 7.05141 35.7203 7.04515 35.7029 7.03225C35.66 7.00037 35.6513 6.94011 35.6836 6.89773L35.7366 6.82752C35.7687 6.78495 35.8296 6.77648 35.8721 6.80836C35.915 6.84023 35.9237 6.90049 35.8914 6.94288L35.8383 7.01309C35.8194 7.03833 35.7903 7.0516 35.7608 7.0516L35.761 7.05141Z"
                  fill="white"
                />
                <path
                  d="M35.9875 6.75867C35.9673 6.75867 35.9469 6.75241 35.9294 6.73951C35.8866 6.70763 35.8778 6.64737 35.9101 6.60499L36.339 6.03687C36.4418 5.88981 36.4815 5.7083 36.4488 5.52882C36.4162 5.34915 36.315 5.19289 36.1642 5.08895L36.131 5.0661C36.087 5.03588 36.076 4.97581 36.1065 4.93214C36.1367 4.88846 36.1972 4.87741 36.2414 4.90781L36.2748 4.93066C36.4683 5.06408 36.5978 5.26438 36.6396 5.49454C36.6814 5.7247 36.6305 5.95726 36.4962 6.14946L36.0653 6.72034C36.0462 6.7454 36.017 6.75867 35.9875 6.75867Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_9485_619">
                  <rect width="40" height="15" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <button
              onClick={() => setIsModalOpen(true)}
              className="p-1.5 bg-azul-400 hover:bg-azul-500 text-white rounded-full transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              title={t("resumoDesk.adLink.add_user")}
              disabled={isRemoving}
            >
              <Plus size={16} />
            </button>
          </div>
          <button
            onClick={onToggle}
            className="text-azul-400 hover:text-azul-500 transition-colors duration-200 text-xs font-medium flex items-center gap-1"
          >
            {isOpen ? t("common:see_less") : t("common:see_more")}
            <svg
              className={`w-4 h-4 transition-transform duration-300 ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {/* Lista de Usuários Compartilhados */}
        <div
          className={`flex flex-col gap-2 overflow-hidden transition-all duration-500 ease-in-out ${
            isOpen
              ? "max-h-[300px] opacity-100 overflow-y-auto pr-2"
              : "max-h-0 opacity-0"
          }`}
        >
          {t("resumoDesk.adLink.users")}
          {normalizedSharedUsers.length > 0 ? (
            normalizedSharedUsers.map((user) => {
              const hasName = user.name && user.last_name;
              const displayName = hasName
                ? `${user.name} ${user.last_name}`
                : user.name
                  ? user.name
                  : user.email;
              const needsRegistration = !hasName;

              return (
                <div
                  key={user.id}
                  className="flex items-center justify-between gap-2"
                >
                  <div className="flex gap-2 items-center bg-cinza-100 rounded-[24px] px-3 py-2 w-full justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="truncate max-w-24" title={displayName}>
                        {displayName}
                      </span>
                      {needsRegistration && (
                        <span
                          className="px-1.5 py-0.5 bg-orange-100 text-orange-800 text-[10px] rounded-full inline-flex items-center gap-1 cursor-pointer hover:bg-orange-200 transition-colors flex-shrink-0"
                          onClick={(e) =>
                            handleCopyRegistrationLink(
                              user.email,
                              user.name ? false : true,
                              e,
                            )
                          }
                          title={t(
                            "resumoDesk.adLink.copy_registration_link_tooltip",
                          )}
                        >
                          {t("resumoDesk.adLink.pending")}
                          <CopyCheck size={10} />
                        </span>
                      )}
                    </div>
                    <button
                      onClick={handleShareAdLink}
                      className="hover:scale-105 duration-300 flex-shrink-0"
                      title={t("resumoDesk.adLink.copy_share_link_tooltip")}
                    >
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.20696 10.8644L10.8638 5.20757M3.08601 7.32866L1.6718 8.74287C0.109702 10.305 0.109249 12.8378 1.67135 14.3999C3.23344 15.962 5.76686 15.9616 7.32896 14.3995L8.74166 12.9854M7.32805 3.08565L8.74226 1.67143C10.3044 0.109336 12.8367 0.109615 14.3988 1.67171C15.9609 3.23381 15.9608 5.76649 14.3987 7.32859L12.9853 8.74276"
                          stroke="#153DCE"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <button
                    onClick={() => handleRemoveUser(user)}
                    disabled={isRemoving}
                    className="text-red-600 hover:scale-105 duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    title={t("resumoDesk.adLink.remove_user")}
                  >
                    <Trash2 width={20} />
                  </button>
                </div>
              );
            })
          ) : (
            <p className="text-center text-cinza-500 text-xs py-4">
              {t("resumoDesk.adLink.no_shared_users")}
            </p>
          )}
        </div>
        {/* Links do ADLink */}
        <div
          className={`flex flex-col gap-2 overflow-hidden transition-all duration-500 ease-in-out ${
            isOpen
              ? "max-h-[200px] opacity-100 overflow-y-auto pr-2"
              : "max-h-0 opacity-0"
          }`}
        >
          {t("resumoDesk.adLink.links")}
          {normalizedLinks?.length > 0 ? (
            normalizedLinks?.map((link, index) => (
              <a
                key={index}
                href={link.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-2 bg-cinza-100 rounded-[24px] px-3 py-2 hover:bg-cinza-200 transition-colors group"
              >
                <span className="truncate text-sm">{link.nome}</span>
                <ExternalLink
                  size={16}
                  className="text-azul-400 group-hover:text-azul-600"
                />
              </a>
            ))
          ) : (
            <p className="text-center text-cinza-500 text-xs py-2">
              {t("resumoDesk.adLink.no_links_added")}
            </p>
          )}
        </div>

        {/* Botões de Ação */}
        <div className="flex gap-2">
          <button
            onClick={handleOpenLinksModal}
            className="w-full bg-azul-400 hover:bg-azul-500 text-white py-2 px-4 rounded-2xl transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2 font-medium"
          >
            <Link size={18} />
            {t("resumoDesk.adLink.links")}
          </button>
        </div>
        {!isOpen && (
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent rounded-b-3xl pointer-events-none" />
        )}
      </div>

      <AddUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        deskId={deskId}
        currentSharedUsers={currentSharedUserIds}
        itemType={itemType}
      />

      {/* Modal de Links */}
      {isLinksModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setIsLinksModalOpen(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {t("resumoDesk.adLink.manage_links")}
              </h2>
              <button
                onClick={() => setIsLinksModalOpen(false)}
                className="p-2 hover:bg-cinza-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              {tempLinks.map((link, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-2 p-3 bg-cinza-100 rounded-2xl"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {t("resumoDesk.adLink.link")} {index + 1}
                    </span>
                    <button
                      onClick={() => handleRemoveLink(index)}
                      className="text-red-600 hover:text-red-700 transition-colors"
                      title={t("resumoDesk.adLink.remove_link")}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder={t("resumoDesk.adLink.link_name_placeholder")}
                    value={link.nome}
                    onChange={(e) =>
                      handleLinkChange(index, "nome", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-cinza-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azul-400 text-sm"
                  />
                  <input
                    type="url"
                    placeholder="https://..."
                    value={link.link}
                    onChange={(e) =>
                      handleLinkChange(index, "link", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-cinza-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azul-400 text-sm"
                  />
                </div>
              ))}

              {tempLinks.length === 0 && (
                <p className="text-center text-cinza-500 text-sm py-4">
                  {t("resumoDesk.adLink.no_links_added_click_to_add")}
                </p>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={handleAddLink}
                className="flex-1 bg-cinza-200 hover:bg-cinza-300 text-black py-2 px-4 rounded-2xl transition-colors flex items-center justify-center gap-2 font-medium"
              >
                <Plus size={18} />
                {t("resumoDesk.adLink.add_link")}
              </button>
              <button
                onClick={handleSaveLinks}
                disabled={isSavingLinks}
                className="flex-1 bg-azul-400 hover:bg-azul-500 text-white py-2 px-4 rounded-2xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isSavingLinks ? t("common:saving") : t("common:save")}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
